// Per-module mock flags (spec §2). Every Phase 2 module defaults to the mock
// because no backend endpoint exists yet; flip a flag to 'false' once that
// module's real API ships, e.g. VITE_MOCK_PAYMENTS=false.
const realApiEnabled = import.meta.env.VITE_USE_REAL_API === 'true';
const flag = (name: string): boolean => (import.meta.env[name] ?? 'true') !== 'false';
const mockOrReal = (name: string): boolean => !realApiEnabled && flag(name);

export const REAL_API_ENABLED = realApiEnabled;
export const MOCK_BENEFICIARIES = mockOrReal('VITE_MOCK_BENEFICIARIES');
export const MOCK_REFERENCE = mockOrReal('VITE_MOCK_REFERENCE');
export const MOCK_PAYMENTS = mockOrReal('VITE_MOCK_PAYMENTS');
export const MOCK_UPI = mockOrReal('VITE_MOCK_UPI');
export const MOCK_STATEMENT = mockOrReal('VITE_MOCK_STATEMENT');
export const MOCK_PROFILE = mockOrReal('VITE_MOCK_PROFILE');
export const MOCK_LOANS = mockOrReal('VITE_MOCK_LOANS');
export const MOCK_BANK_SERVICES = mockOrReal('VITE_MOCK_BANK_SERVICES');
export const MOCK_BILL_PAY = mockOrReal('VITE_MOCK_BILL_PAY');
export const MOCK_INVESTMENTS = mockOrReal('VITE_MOCK_INVESTMENTS');
export const MOCK_CREDIT_CARDS = mockOrReal('VITE_MOCK_CREDIT_CARDS');
export const CC_NETWORK_CHOICE_ENABLED = flag('VITE_CC_NETWORK_CHOICE_ENABLED');
export const FEATURE_TEMPORARY_PASSWORD = flag('VITE_FEATURE_TEMPORARY_PASSWORD');
export const FEATURE_UPI_PIN = flag('VITE_FEATURE_UPI_PIN');
export const FEATURE_PAYMENTS = flag('VITE_FEATURE_PAYMENTS');

// Phase 5 capability switch. Mock mode keeps unfinished journeys available;
// real API mode hides journeys whose backend endpoints are not ready.
export const features = {
  lockers: !realApiEnabled && flag('VITE_FEATURE_LOCKERS'),
  fatca: !realApiEnabled && flag('VITE_FEATURE_FATCA'),
  gst: !realApiEnabled && flag('VITE_FEATURE_GST'),
  favourites: !realApiEnabled && flag('VITE_FEATURE_FAVOURITES'),
  taxCertificates: !realApiEnabled && flag('VITE_FEATURE_TAX_CERTIFICATES'),
  chat: !realApiEnabled && flag('VITE_FEATURE_CHAT'),
  upiCollect: !realApiEnabled && flag('VITE_FEATURE_UPI_COLLECT'),
  upiSplit: !realApiEnabled && flag('VITE_FEATURE_UPI_SPLIT'),
  scheduledPayments: !realApiEnabled && flag('VITE_FEATURE_SCHEDULED_PAYMENTS'),
  upiPayments: flag('VITE_FEATURE_UPI_PAYMENTS'),
  trackByMobile: flag('VITE_FEATURE_TRACK_BY_MOBILE'),
  cardStatement: !realApiEnabled && flag('VITE_FEATURE_CARD_STATEMENT'),
  cardDeliveryTracking: !realApiEnabled && flag('VITE_FEATURE_CARD_DELIVERY_TRACKING'),
  creditCardExtras: !realApiEnabled && flag('VITE_FEATURE_CREDIT_CARD_EXTRAS'),
  loanExtras: !realApiEnabled && flag('VITE_FEATURE_LOAN_EXTRAS'),
  investmentExtras: !realApiEnabled && flag('VITE_FEATURE_INVESTMENT_EXTRAS'),
};

// Dev-only: when true, the applicant home auto-runs the bank-verification
// (review start → video-KYC sign-off → approve) for an application waiting at
// "Bank verification", so the flow reaches Active without an employee login.
// Defaults ON in dev so onboarding completes end-to-end; set
// VITE_DEV_AUTO_VERIFY=false to require the manual dev-panel action instead.
// Never enable in production — approval is an employee action.
export const DEV_AUTO_VERIFY = flag('VITE_DEV_AUTO_VERIFY');

// Dev-only dummy bank verification: when the application reaches "Bank
// verification" the applicant home marks it approved LOCALLY (localStorage) —
// no review API, no employee token — and moves straight to login. Defaults ON
// for dev; set VITE_MOCK_BANK_VERIFICATION=false to use the real review
// endpoints (DEV_AUTO_VERIFY above).
export const MOCK_BANK_VERIFICATION = flag('VITE_MOCK_BANK_VERIFICATION');
